import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/Bits";
import { Send, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

export default function ContactUs() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const sendMut = useMutation({
    mutationFn: async () => (await api.post("/contact", form)).data,
    onSuccess: () => {
      setSent(true);
      toast.success("Message sent — you'll hear back within 24 hours.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send"),
  });

  if (sent) {
    return (
      <div>
        <PageHeader eyebrow="CONTACT" title="Message Sent" />
        <div className="p-8">
          <div className="max-w-xl border border-[rgba(34,197,94,0.40)] bg-emerald-500/5 p-8 text-center" data-testid="contact-sent">
            <Check className="h-10 w-10 text-[color:var(--accent)] mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-lg font-mono-num text-[color:var(--fg)] mb-2">Thanks for reaching out.</div>
            <div className="text-sm text-[color:var(--fg-2)]">We&apos;ll be in touch within 24 hours at <span className="text-[color:var(--accent)]">{form.email}</span>.</div>
            <button
              onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
              className="mt-6 text-xs text-[color:var(--fg-3)] hover:text-[color:var(--accent)] font-mono-num"
              data-testid="contact-send-another"
            >
              SEND ANOTHER MESSAGE
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="CONTACT" title="Get In Touch" />
      <div className="p-8">
        <div className="max-w-2xl">
          <Card eyebrow="MESSAGE US" title="Bug, feature request, billing, or just say hi">
            <p className="text-xs text-[color:var(--fg-3)] font-mono-num mb-5 leading-relaxed">
              We read every message. Expect a reply within 24 hours.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="label-eyebrow mb-1.5">YOUR NAME</div>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num"
                  placeholder="Optional"
                />
              </div>
              <div>
                <div className="label-eyebrow mb-1.5">YOUR EMAIL *</div>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num"
                  placeholder="trader@example.com"
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="label-eyebrow mb-1.5">SUBJECT</div>
              <input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="contact-subject"
                className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num"
                placeholder="What's this about?"
              />
            </div>
            <div className="mt-4">
              <div className="label-eyebrow mb-1.5">MESSAGE *</div>
              <textarea
                required
                rows={8}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-3 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num leading-relaxed resize-none"
                placeholder="Tell us what's on your mind…"
              />
              <div className="mt-1 text-[10px] text-[color:var(--fg-3)] font-mono-num text-right">{form.message.length} / 5000</div>
            </div>
            <button
              type="button"
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || !form.email.trim() || form.message.trim().length < 2}
              data-testid="contact-send-btn"
              className="mt-2 inline-flex items-center gap-2 bg-[color:var(--accent)] text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
              {sendMut.isPending ? "SENDING…" : "SEND MESSAGE"}
            </button>
            {/* Helper — tells the user WHY the button is disabled instead of leaving them guessing */}
            {(() => {
              const emailMissing = !form.email.trim();
              const msgTooShort = form.message.trim().length < 2;
              if (!emailMissing && !msgTooShort) return null;
              const reasons = [];
              if (emailMissing) reasons.push("your email");
              if (msgTooShort) reasons.push("a message");
              return (
                <div className="mt-2 text-[11px] text-[color:var(--fg-3)]" data-testid="contact-send-hint">
                  Add {reasons.join(" and ")} to enable send.
                </div>
              );
            })()}
          </Card>
        </div>
      </div>
    </div>
  );
}
